import {
  Box,
  Link,
  Stack,
  Typography,
  Grid,
  Button,
  Divider,
} from "@mui/material";
import { SyntheticEvent, useState } from "react";
import { InfoAccordion } from "../components/InfoAccordion.tsx";
import { AccordionInfo } from "../types/AccordionInfo.ts";
import { EventCategory } from "@labs/shared/types/EventCategory.ts";
import emailSmallImage from "../assets/emailSmall.png";
import { Link as RouterLink } from "react-router";
import { trackEvent } from "../util/analytics.ts";

const faqs: AccordionInfo[] = [
  {
    title: "What is this emissions reduction planner?",
    body: (
      <Typography variant="body1">
        This planner helps you understand which decarbonization solutions will
        have the largest impact on your carbon footprint. You can also use it to
        explore the tradeoffs between onsite energy efficiency or
        electrification projects vs supply-side clean power purchasing or
        offsets.
      </Typography>
    ),
  },
  {
    title: "Who should use this planner?",
    body: (
      <Typography variant="body1">
        Sustainability professionals who want to get started with a simple
        emissions reduction planning process, learn about the impact of various
        solutions on their carbon footprint, and/or test whether their current
        emissions reduction plan is expected to achieve the desired target.
      </Typography>
    ),
  },
  {
    title: "What are the steps?",
    body: (
      <Box
        component={"ol"}
        sx={{
          margin: 0,
        }}
      >
        <li>
          Set a decarbonization target.{" "}
          <em>Don't have a target? Use our presets.</em>
        </li>
        <li>
          Set emissions profile.{" "}
          <em>
            Don't have emissions data on hand? Select a profile by industry
            type.
          </em>
        </li>
        <li>
          Select your solutions.{" "}
          <em>
            Not sure which solutions to choose? Use our recommendations and
            explore different scenarios.
          </em>
        </li>
      </Box>
    ),
  },
  {
    title: "What information do I need to provide?",
    body: (
      <Typography variant="body1">
        Only your facility type. To personalize your results, you can enter your
        organization’s scope 1 and 2 emissions reduction target and emissions
        profile. Your emissions profile is the % contribution of total Scope 1 +
        2 emissions by fuel type. We won’t ask you for your absolute emissions
        footprint.
      </Typography>
    ),
  },
  {
    title: "How long will it take?",
    body: (
      <Typography variant="body1">
        You can generate an emissions reduction plan in as little as 10 minutes,
        then iterate on scenarios as much as you’d like.
      </Typography>
    ),
  },
  {
    title: "What will I get?",
    body: (
      <Stack direction={"column"} spacing={2}>
        <Typography variant="body1">
          You’ll get a copy of your plan via email, so you can share with
          colleagues and revisit later. Here's an example:
        </Typography>
        <img src={emailSmallImage} width={"50%"} alt="Example plan email" />
      </Stack>
    ),
  },
  {
    title: "What will you do with my data?",
    body: (
      <Typography variant="body1">
        We treat your data in accordance with our{" "}
        <Link href={"https://cascadeenergy.com/privacy-policy"} target="_blank">
          privacy policy
        </Link>
        . We do not sell or otherwise trade the information we collect during
        the process of creating your emissions reduction plan.
      </Typography>
    ),
  },
  {
    title: "Can I trust the results?",
    body: (
      <Typography variant="body1">
        Cascade Energy built this planner based on 30 years of experience
        working with thousands of commercial and industrial organizations. We
        hope this planner helps visualize how hypothetically applying various
        decarbonization solutions may impact your emissions footprint. We do not
        collect enough data in this planner to validate a target or a plan, and
        this website should not be construed as an assessment or validation of a
        target or plan.
      </Typography>
    ),
  },
  {
    title: "Are scope 3 emissions included?",
    body: (
      <Typography variant="body1">
        No, this emissions reduction planner only includes scope 1 and 2
        emissions.
      </Typography>
    ),
  },
  {
    title:
      "Can I use this planner to get buy-in and alignment across my organization?",
    body: (
      <Typography variant="body1">
        Absolutely! Use this planner as an educational tool to uncover
        misconceptions or misalignment about the impact or relative cost of
        various solutions. Share the report with others to get aligned on the
        best decarbonization pathways.
      </Typography>
    ),
  },
];

const LearnMore = () => {
  const [expanded, setExpanded] = useState<string | false>(false);
  const handleChange =
    (panel: string) =>
    (_event: SyntheticEvent, isExpanded: boolean): void => {
      setExpanded(isExpanded ? panel : false);
    };

  return (
    <Stack direction={"column"} spacing={8} alignItems={"center"}>
      <Stack direction={"column"} sx={{ maxWidth: 1000 }}>
        {faqs.map((faq, index) => (
          <InfoAccordion
            key={faq.title}
            info={faq}
            analyticsCategory={EventCategory.FAQ}
            expanded={expanded === `panel${index}`}
            onChange={handleChange(`panel${index}`)}
          />
        ))}
      </Stack>
      <Grid container rowSpacing={4}>
        <Grid size={{ xs: 12, md: 6 }} sx={{ alignItems: "center" }}>
          <Stack direction={"row"} spacing={2} alignItems={"flex-start"}>
            <Typography variant={"body1"}>
              Would you like to speak to a decarbonization expert?
            </Typography>
            <Button
              variant={"contained"}
              component={RouterLink}
              to="/guided-tour"
              onClick={() => {
                trackEvent(
                  EventCategory.GuidedTour,
                  "Clicked Link",
                  window.location.href,
                );
              }}
              sx={{ width: "100%", textAlign: "center" }}
            >
              Request a Guided Tour
            </Button>
          </Stack>
        </Grid>
        <Grid
          size={1}
          sx={{
            display: { xs: "none", md: "flex" },
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <Divider orientation={"vertical"} />
        </Grid>
        <Grid size={{ xs: 12, md: 5 }} sx={{ alignItems: "center" }}>
          <Stack direction={"row"} spacing={2} alignItems={"flex-start"}>
            <Typography variant={"body1"}>
              Get a free emissions reduction report in as little as 10 minutes
            </Typography>
            <Button
              variant={"outlined"}
              component={RouterLink}
              to={"/target"}
              sx={{ whiteSpace: "nowrap", width: "100%" }}
            >
              Get started
            </Button>
          </Stack>
        </Grid>
      </Grid>
    </Stack>
  );
};
export default LearnMore;
